<script setup lang="ts">
import DonutChartWithLegend from '@/components/Globals/Charts/DonutChartWithLegend.vue'
import ProgressBarWithLabel from '@/components/Globals/ProgressBarWithLabel.vue'
import IcEmptyBox from '@/components/Icons/IcEmptyBox.vue'
import { useCompanyStore } from '@/store/company'
import { useMaintenanceStore } from '@/store/maintenance'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import MaintenanceReportSensorChart from './MaintenanceReportSensorChart.vue'
import MaintenanceReportStatCard from './MaintenanceReportStatCard.vue'

const { t } = useI18n()

const maintenanceStore = useMaintenanceStore()
const { getMaintenanceReport, hasMaintenanceReportLoaded } = storeToRefs(maintenanceStore)
const { clearMaintenanceReport } = maintenanceStore

const companyStore = useCompanyStore()
const { getCurrentCompany } = storeToRefs(companyStore)

watch(
  () => getCurrentCompany.value?.id,
  (newId, oldId) => {
    if (oldId !== undefined && newId !== oldId) {
      clearMaintenanceReport()
    }
  },
)

const categorySegments = computed(() => {
  const report = getMaintenanceReport.value
  if (!report) return []
  const c = report.by_category
  return [
    { label: t('maintenance_report.body.result.categories.preventive'), value: c.preventive },
    { label: t('maintenance_report.body.result.categories.predictive'), value: c.predictive },
    { label: t('maintenance_report.body.result.categories.corrective'), value: c.corrective },
    { label: t('maintenance_report.body.result.categories.unknown'), value: c.unknown },
  ]
})

const categoryTotal = computed(() => categorySegments.value.reduce((s, x) => s + x.value, 0))

const avgRepairDisplay = computed(() => {
  const v = getMaintenanceReport.value?.average_repair_duration_hours
  if (v === null || v === undefined) return t('maintenance_report.body.result.cards.avg_repair_na')
  return `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${t('maintenance_report.body.result.cards.avg_repair_unit')}`
})
</script>

<template>
  <div class="flex min-w-0 flex-col justify-center gap-4 px-2 py-6.5 lg:px-10">
    <div v-if="!hasMaintenanceReportLoaded" class="flex flex-col items-center text-center">
      <IcEmptyBox class="h-90 pb-12 pt-24" />
      <p class="text-gray font-satoshi text-title-xsm font-bold">
        {{ t('maintenance_report.body.result.no_data') }}
      </p>
      <p class="font-satoshi text-title-xs font-medium text-gray">
        {{ t('maintenance_report.body.result.no_data_hint') }}
      </p>
    </div>

    <template v-else-if="getMaintenanceReport">
      <div class="flex w-full min-w-0 flex-col gap-8">
        <h2 class="shrink-0 font-satoshi text-title-sm font-bold leading-snug text-black">
          {{ t('maintenance_report.body.result.title') }}
        </h2>

        <div
          class="grid w-full grid-flow-col grid-cols-1 items-stretch gap-8 sm:grid sm:grid-flow-row sm:grid-cols-2 lg:grid-cols-3">
          <MaintenanceReportStatCard
            :label="t('maintenance_report.body.result.cards.orders_created')"
            :value="getMaintenanceReport.orders_created_count" />

          <MaintenanceReportStatCard
            :label="t('maintenance_report.body.result.cards.orders_completed')"
            :value="getMaintenanceReport.orders_completed_count">
            <ProgressBarWithLabel
              :completed="getMaintenanceReport.orders_completed_count"
              :total="getMaintenanceReport.orders_created_count" />
          </MaintenanceReportStatCard>

          <MaintenanceReportStatCard
            :label="t('maintenance_report.body.result.cards.avg_repair_hours')"
            :value="avgRepairDisplay" />
        </div>

        <div
          class="grid min-w-0 grid-cols-1 items-stretch gap-8 lg:grid-cols-2 [&>*]:min-w-0">
          <DonutChartWithLegend
            v-if="categoryTotal > 0"
            :title="t('maintenance_report.body.result.charts.by_category')"
            :segments="categorySegments" />

          <MaintenanceReportSensorChart
            v-if="getMaintenanceReport.by_sensor.length"
            :items="getMaintenanceReport.by_sensor"
            :chart-title="t('maintenance_report.body.result.charts.by_sensor')"
            :series-name="t('maintenance_report.body.result.charts.sensor_series')" />
        </div>

        <div v-if="getMaintenanceReport.by_user.length" class="flex w-full flex-col gap-8">
          <h2 class="shrink-0 text-title-sm font-bold leading-snug text-black">
            {{ t('maintenance_report.body.result.table.title') }}
          </h2>
          <div class="w-full bg-white p-4 shadow-md">
            <div class="overflow-x-auto">
              <table class="w-full border-collapse text-left">
                <thead>
                  <tr class="border-b border-stroke bg-gray-2">
                    <th class="px-4 py-3 text-title-xsm font-semibold text-black">
                      {{ t('maintenance_report.body.result.table.technician') }}
                    </th>
                    <th class="px-4 py-3 text-title-xsm font-semibold text-black">
                      {{ t('maintenance_report.body.result.table.orders_count') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in getMaintenanceReport.by_user"
                    :key="row.user_id"
                    class="border-b border-stroke">
                    <td class="px-4 py-3 text-title-xsm text-gray">
                      {{ row.name }}
                    </td>
                    <td class="px-4 py-3 text-title-xsm font-medium text-black">
                      {{ row.count }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
